import { MediaType } from '@qse/api'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { FileExplorer } from '@/components'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

interface SearchValues {
  search: string
  fileType?: string
  isDir?: boolean
  createdFrom?: string
  createdTo?: string
}

const Favorites = () => {
  const query = useQuery()
  const searchQuery = query.get('query') || ''
  const fileType = query.get('fileType') || ''
  const isDir = query.get('isDir') || ''
  const createdFrom = query.get('createdFrom') || ''
  const createdTo = query.get('createdTo') || ''

  const searchValues = useMemo<SearchValues>(
    () => ({
      search: searchQuery,
      fileType: fileType || undefined,
      isDir: isDir === 'true' ? true : undefined,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
    }),
    [searchQuery, fileType, isDir, createdFrom, createdTo],
  )

  // Search parameters for FileExplorer
  const searchParams = useMemo(
    () => ({
      limit: 50,
      location: 'FAVORITE' as const, // Use location to filter for favorites
      search: searchValues.search || undefined,
      isDir: searchValues.isDir === true ? true : undefined,
      mediaType:
        searchValues.isDir === true ? undefined : (searchValues.fileType as MediaType) || undefined,
      createdFrom: searchValues.createdFrom || undefined,
      createdTo: searchValues.createdTo || undefined,
    }),
    [searchValues],
  )

  const emptyStateConfig = {
    icon: (
      <svg
        className="w-12 h-12 text-gray-400 dark:text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
    title: 'No favorites yet',
    description: 'Star files and folders to add them to your favorites for quick access.',
  }

  // Generate title based on active filters
  const getTitle = () => {
    const parts = []

    if (searchValues.search) {
      parts.push(`Search: "${searchValues.search}"`)
    }

    if (searchValues.fileType) {
      const fileTypeLabels: Record<string, string> = {
        FOLDER: 'Folders',
        DOCUMENT: 'Documents',
        IMAGE: 'Images',
        VIDEO: 'Videos',
        AUDIO: 'Audio',
        OTHER: 'Other Files',
      }

      parts.push(`Type: ${fileTypeLabels[searchValues.fileType] || searchValues.fileType}`)
    }

    if (searchValues.createdFrom || searchValues.createdTo) {
      parts.push('Date Filtered')
    }

    return parts.length > 0 ? parts.join(' | ') : 'Favorites'
  }

  return (
    <div className="flex flex-col text-gray-900 dark:text-white">
      {/* Header */}
      <div className="lg:flex flex-col hidden">
        <div className="px-4 md:px-[30px] w-fit">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{getTitle()}</h1>
          {/* <p className="text-gray-600 dark:text-gray-400">Your starred files and folders</p> */}
        </div>
        <div className="px-4 md:px-[30px]">
          <p className="text-gray-600 dark:text-gray-400">Your starred files and folders</p>
        </div>
      </div>

      <div className="flex-1">
        <FileExplorer
          emptyStateConfig={emptyStateConfig}
          searchParams={searchParams}
          title=""
          uploadEnabled={false}
        />
      </div>
    </div>
  )
}

export default Favorites
